'use client';

import Image from 'next/image';

import {
  DownloadIcon,
  PictureAsPdfIcon,
  ImageIcon,
  WordIcon,
  ExcelIcon,
  DocIcon,
} from '@/components/atoms/Icons';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const IMAGE_EXTENSIONS_TAG = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'ico'];
const WORD_EXTENSIONS = ['doc', 'docx', 'rtf'];
const EXCEL_EXTENSIONS = ['xls', 'xlsx', 'csv'];
const TEXT_EXTENSIONS = ['txt', 'md', 'json'];

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const getExtension = (fileName) => (fileName ? fileName.toLowerCase().split('.').pop() : '');

const buildFileUrl = (url) => {
  if (!url) return '';

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (url.startsWith('/doccenter')) {
    return url;
  }

  if (typeof window === 'undefined') return url;

  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

const isImageFile = (fileName) => IMAGE_EXTENSIONS_TAG.includes(getExtension(fileName));

const getIconComponentByFileName = (fileName) => {
  const extension = getExtension(fileName);

  if (!extension) return <DocIcon />;
  if (extension === 'pdf') return <PictureAsPdfIcon />;
  if (IMAGE_EXTENSIONS.includes(extension)) return <ImageIcon />;
  if (WORD_EXTENSIONS.includes(extension)) return <WordIcon />;
  if (EXCEL_EXTENSIONS.includes(extension)) return <ExcelIcon />;
  if (TEXT_EXTENSIONS.includes(extension)) return <DocIcon />;

  return <DocIcon />;
};

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function Media({ mediaItems = [] }) {
  const handleDownload = async (url, fileName) => {
    if (!url) return;

    try {
      const fileUrl = buildFileUrl(url);

      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: { Accept: '*/*' },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || fileUrl.split('/').pop() || 'media-download';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <section id="media" className="section-spacing scroll-mt-20 md:scroll-mt-0 mt-2">
      <div className="container-wrap">
        <h2 className="md:text-center xl:text-[54px] lg:text-[48px] sm:text-[40px] text-[26px] leading-[1em] font-semibold text-gray-900 2xl:mb-10.5 lg:mb-8 md:mb-6 mb-5">
          Media
        </h2>

        {mediaItems.length === 0 ? (
          <p className="text-center text-gray-500 py-2">No media items available.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-8 gap-5 lg:gap-6 xl:gap-8 2xl:gap-9 md:mx-0 -mx-4">
            {mediaItems.map((item) => {
              const { id, name, fileName, url } = item;
              const title = name || fileName || '';
              const isImage = isImageFile(fileName);
              const previewUrl = isImage ? buildFileUrl(url) : '';

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleDownload(url, fileName)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleDownload(url, fileName);
                    }
                  }}
                  disabled={!url}
                  aria-label={`Download ${title}`}
                  className="block text-left w-full transition-all duration-300 hover:shadow-[-1px_6px_30px_0_rgba(0,0,0,0.2)] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {/* Preview */}
                  <div className="md:aspect-[380/348] aspect-[177/162] rounded-xl border border-[#DDDDDD] bg-white flex items-center justify-center 2xl:p-12 lg:p-10 p-6">
                    {isImage && previewUrl ? (
                      <Image
                        src={previewUrl}
                        alt={title || 'Media item'}
                        width={240}
                        height={171}
                        className="object-contain"
                      />
                    ) : (
                      <div className="text-gray-400">{getIconComponentByFileName(fileName)}</div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="md:px-0 px-4">
                    <div className="md:px-2 xl:pb-2.5 pb-1.5 xl:pt-5 pt-3.5 border-b border-[#DDDDDD]">
                      <p className="text-sm md:text-base font-medium text-[#323232]">{title}</p>

                      <div className="w-full flex items-center justify-between gap-2 xl:pt-4 md:pt-3 pt-2 text-sm md:text-base text-[#626262]">
                        Download
                        <DownloadIcon className="md:h-4.25 md:w-4.25 h-2.5 w-2.5" />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
